/**
 * Lightweight page-context registry for the HFCL Chat Bot.
 *
 * Pages register what the user is currently looking at (route, active
 * tab, expanded accordion, etc.) by calling `useHfclChatContext(...)`.
 * The chat reads this when sending a message and passes it to the
 * backend, so Ollama can disambiguate "edit" / "open" requests based on
 * what's actually on screen.
 *
 * Pages can also expose imperative setters under window.hfcl* so the
 * chat can drive UI state directly (e.g. expand a Global Settings
 * accordion before clicking the Edit button inside it).
 */

const KEY = '__HFCL_CHAT_CONTEXT__';

const ensureStore = () => {
	if (typeof window === 'undefined') return null;
	if (!window[KEY]) {
		window[KEY] = {
			route: typeof window.location !== 'undefined' ? window.location.pathname : null,
			tab: null,
			section: null,
			editActionId: null,
			extras: {},
			updatedAt: Date.now(),
		};
	}
	return window[KEY];
};

/**
 * Read the current registered context (or an empty object if nothing
 * has been registered yet).
 *
 * @return {Object}
 */
export const getHfclChatContext = () => {
	const store = ensureStore();
	return store ? { ...store } : {};
};

/**
 * Merge `patch` into the live context. Returns the new full context.
 *
 * @param {Object} patch
 * @return {Object}
 */
export const setHfclChatContext = (patch) => {
	const store = ensureStore();
	if (!store) return {};
	if (patch && typeof patch === 'object') {
		Object.assign(store, patch, { updatedAt: Date.now() });
	}
	const snapshot = { ...store };
	if (typeof window !== 'undefined') {
		window.dispatchEvent(
			new CustomEvent('hfcl-chat-context-change', { detail: snapshot })
		);
	}
	return snapshot;
};

/**
 * Replace the whole context (used on unmount to clear stale info).
 *
 * @param {Object} next
 */
export const resetHfclChatContext = (next = {}) => {
	if (typeof window === 'undefined') return;
	window[KEY] = {
		route: null,
		tab: null,
		section: null,
		editActionId: null,
		extras: {},
		updatedAt: Date.now(),
		...next,
	};
};
